import { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { createRootRouteWithContext, HeadContent, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import React from 'react';
import { Provider } from 'react-redux';

import { getEnv } from '@/app/conf/env';
import { AppToastContainer } from '@/app/lib/react-toastify';
import { queryPersister } from '@/app/lib/tanstack-query';
import { CurrentUserResponse } from '@/app/network/models/v1/user';
import type { store } from '@/app/stores/redux-store';
import { Spinner } from '@/common/components/spinner';
import { RootNotFound } from '@/shared/components/root-not-found';
import { MainLayout } from '@/shared/layouts/main-layout';

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  store: typeof store;
  ensureUser: () => Promise<CurrentUserResponse>;
}>()({
  component: RootComponent,
  notFoundComponent: RootNotFound
});

function RootComponent() {
  const { queryClient, store } = Route.useRouteContext();
  return (
    <Provider store={store}>
      <PersistQueryClientProvider client={queryClient} persistOptions={{ persister: queryPersister, buster: getEnv().APP_VERSION }}>
        {getEnv().DEBUG_TANSTACK_QUERY && (<ReactQueryDevtools />)}
        <HeadContent />
        <AppToastContainer />
        <React.Suspense
          fallback={(
            <div className="flex h-screen items-center justify-center">
              <Spinner size="xl" />
              <p className="text-center text-zinc-950/50">Loading...</p>
            </div>
          )}
        >
          <MainLayout>
            <Outlet />
            {getEnv().DEBUG_TANSTACK_ROUTER && (<TanStackRouterDevtools />)}
          </MainLayout>
        </React.Suspense>
      </PersistQueryClientProvider>
    </Provider>
  );
}
